import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { useData } from '../../context/DataContext';
import { coursesService } from '../../services/firestoreService';
import CourseComponentsManager from './CourseComponentsManager';
import './CoursesManagement.css';

const courseContentButtons = [
  { type: 'quizQuestions', label: '🎯 Quiz Question' },
  { type: 'exercises', label: '💪 Exercise' },
  { type: 'matchingPairs', label: '🔄 Matching Pair' },
  { type: 'codeExamples', label: '💻 Code Example' },
  { type: 'learningCards', label: '📚 Learning Card' },
  { type: 'syntaxGuessCards', label: '🎲 Syntax Guess' }
];

const CoursesManagement = () => {
  const { user } = useUser();
  const { courses, loading } = useData();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showComponentConfig, setShowComponentConfig] = useState(false);
  const [configuringCourse, setConfiguringCourse] = useState(null);
  const [configuringTopic, setConfiguringTopic] = useState(null);
  const [expandedCourses, setExpandedCourses] = useState(new Set()); // Track expanded course IDs
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    courseType: 'programming', // New field for course type
    category: '', // New field for category
    level: 'beginner', // New field for difficulty level
    topics: [{ name: '', description: '', hours: '', date: '', sampleCode: '' }]
  });


  const handleAddTopic = () => {
    setFormData({
      ...formData,
      topics: [...formData.topics, { name: '', description: '', hours: '2', date: '', sampleCode: '' }]
    });
  };


  const handleTopicChange = (index, field, value) => {
    const newTopics = [...formData.topics];
    if (field === 'hours') {
      if (value === '') {
        newTopics[index][field] = '';
      } else {
        const numericValue = Math.max(2, parseFloat(value) || 0);
        newTopics[index][field] = numericValue.toString();
      }
    } else {
      newTopics[index][field] = value;
    }
    setFormData({ ...formData, topics: newTopics });
  };

  const handleRemoveTopic = (index) => {
    const newTopics = formData.topics.filter((_, i) => i !== index);
    setFormData({ ...formData, topics: newTopics });
  };

  const handleAddContentNavigation = (course, topicName, contentType) => {
    if (!topicName) {
      alert('Please choose a topic before creating content.');
      return;
    }

    const topic = (course.topics || []).find(t => t.name === topicName);
    const topicHours = topic ? (parseFloat(topic.hours) || 0) : 0;

    if (topicHours < 2) {
      alert('Topics must be at least 2 hours long before adding interactive content.');
      return;
    }

    // Navigate to Interactive Learning Content Studio
    // Pass course and topic as URL params or state
    const basePath = user?.type === 'admin' ? '/admin/interactive-learning' : '/coach/content-studio';
    navigate(basePath, {
      state: {
        courseId: course.id,
        topicName: topicName,
        showAddContent: true,
        addType: contentType
      }
    });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const invalidTopic = formData.topics.some(
        (t) => t.name.trim() !== '' && ((parseFloat(t.hours) || 0) < 2)
      );

      if (invalidTopic) {
        alert('Each topic must have a duration of at least 2 hours. Please update the topic hours.');
        setSaving(false);
        return;
      }

      const creatorId = editingCourse?.createdBy || user?.id || 'unknown';
      const creatorName = editingCourse?.createdByName || user?.name || user?.email?.split('@')[0] || 'Coach';
      const creatorEmail = editingCourse?.createdByEmail || user?.email || '';

      const courseData = {
        title: formData.title,
        description: formData.description,
        courseType: formData.courseType,
        category: formData.category,
        level: formData.level,
        topics: formData.topics
          .filter(t => t.name.trim() !== '')
          .map(t => ({
            name: t.name,
            description: t.description,
            hours: Math.max(2, parseFloat(t.hours) || 0),
            date: t.date || '',
            sampleCode: t.sampleCode || ''
          })),
        createdBy: creatorId,
        createdByName: creatorName,
        createdByEmail: creatorEmail
      };

      let savedCourseId = editingCourse ? editingCourse.id : null;

      if (editingCourse) {
        await coursesService.updateCourse(editingCourse.id, courseData);
      } else {
        savedCourseId = await coursesService.createCourse(courseData);
      }


      resetForm();
    } catch (error) {
      console.error('Error saving course:', error);
      alert('Error saving course. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      courseType: 'programming',
      category: '',
      level: 'beginner',
      topics: [{ name: '', description: '', hours: '2', date: '', sampleCode: '' }]
    });
    setShowForm(false);
    setEditingCourse(null);
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      title: course.title,
      description: course.description,
      courseType: course.courseType || 'programming',
      category: course.category || '',
      level: course.level || 'beginner',
      topics: course.topics.length > 0 
        ? course.topics.map(t => ({
            name: t.name,
            description: t.description,
            hours: (() => {
              const numericHours = parseFloat(t.hours);
              if (Number.isNaN(numericHours)) {
                return '2';
              }
              return Math.max(2, numericHours).toString();
            })(),
            date: t.date || '',
            sampleCode: t.sampleCode || ''
          }))
        : [{ name: '', description: '', hours: '2', date: '', sampleCode: '' }]
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        await coursesService.deleteCourse(id);
      } catch (error) {
        console.error('Error deleting course:', error);
        alert('Error deleting course. Please try again.');
      }
    }
  };

  const handleConfigureCourseComponents = (course) => {
    setConfiguringCourse(course.id);
    setConfiguringTopic(null);
    setShowComponentConfig(true);
  };

  const handleConfigureTopicComponents = (course, topicIndex) => {
    setConfiguringCourse(course.id);
    setConfiguringTopic(topicIndex);
    setShowComponentConfig(true);
  };

  const handleComponentConfigSave = () => {
    setShowComponentConfig(false);
    setConfiguringCourse(null);
    setConfiguringTopic(null);
  };

  const handleComponentConfigCancel = () => {
    setShowComponentConfig(false);
    setConfiguringCourse(null);
    setConfiguringTopic(null);
  };

  const toggleCourseExpansion = (courseId) => {
    setExpandedCourses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(courseId)) {
        newSet.delete(courseId);
      } else {
        newSet.add(courseId);
      }
      return newSet;
    });
  };

  return (
    <div className="courses-management">
      <div className="page-header">
        <h1>📚 Courses Management</h1>
        <button className="add-btn" onClick={() => { resetForm(); setShowForm(true); }}>
          ➕ Add New Course
        </button>
      </div>


      {showForm && (
        <div className="modal-overlay" onClick={() => resetForm()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingCourse ? 'Edit Course' : 'Create New Course'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Course Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Course Type *</label>
                  <select
                    value={formData.courseType}
                    onChange={(e) => setFormData({ ...formData, courseType: e.target.value })}
                    required
                  >
                    <option value="programming">💻 Programming & Coding</option>
                    <option value="mathematics">🔢 Mathematics</option>
                    <option value="language">📖 Language & Communication</option>
                    <option value="robotics">🤖 Robotics & Hardware</option>
                    <option value="science">🔬 Science</option>
                    <option value="arts">🎨 Arts & Design</option>
                    <option value="business">💼 Business & Finance</option>
                    <option value="other">📚 Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Level *</label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    required
                  >
                    <option value="beginner">🌱 Beginner</option>
                    <option value="intermediate">🌿 Intermediate</option>
                    <option value="advanced">🌳 Advanced</option>
                    <option value="expert">🏆 Expert</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Category/Subcategory</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., English Speaking, Grammar, Python, AI, Math Basics, etc."
                />
                <small className="form-hint">Specify the specific area within the course type</small>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  placeholder="Provide a detailed description of what students will learn..."
                />
              </div>
              <div className="topics-section">
                <label>Topics *</label>
                {formData.topics.map((topic, index) => (
                  <div key={index} className="topic-item">
                    <input
                      type="text"
                      placeholder="Topic name"
                      value={topic.name}
                      onChange={(e) => handleTopicChange(index, 'name', e.target.value)}
                      required
                    />
                    <textarea
                      placeholder="Topic description"
                      value={topic.description}
                      onChange={(e) => handleTopicChange(index, 'description', e.target.value)}
                      rows="2"
                    />
                    <input
                      type="number"
                      placeholder="Hours (min 2)"
                      value={topic.hours}
                      onChange={(e) => handleTopicChange(index, 'hours', e.target.value)}
                      min="2"
                      step="0.5"
                      title="Each topic must be scheduled for at least 2 hours"
                    />
                    <input
                      type="date"
                      placeholder="Topic Date"
                      value={topic.date}
                      onChange={(e) => handleTopicChange(index, 'date', e.target.value)}
                      title="When will this topic be covered?"
                    />
                    <div className="sample-code-section">
                      <label>💻 Sample Code (Optional)</label>
                      <textarea
                        placeholder="Enter sample code for this topic..."
                        value={topic.sampleCode || ''}
                        onChange={(e) => handleTopicChange(index, 'sampleCode', e.target.value)}
                        rows="6"
                        className="code-textarea"
                      />
                      <small className="form-hint">Students will see this code in the classroom section</small>
                    </div>
                    {formData.topics.length > 1 && (
                      <button type="button" onClick={() => handleRemoveTopic(index)} className="remove-btn">
                        ❌ Remove Topic
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={handleAddTopic} className="add-topic-btn">
                  ➕ Add Topic
                </button>
              </div>
              <div className="form-actions">
                <button type="submit" className="save-btn" disabled={saving}>
                  {saving ? '💾 Saving...' : '💾 Save'}
                </button>
                <button type="button" onClick={resetForm} className="cancel-btn" disabled={saving}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="courses-management-list">
        {loading ? (
          <div className="empty-state">
            <p>Loading courses...</p>
          </div>
        ) : courses.length === 0 ? (
          <div className="empty-state">
            <p>No courses yet. Create your first course!</p>
          </div>
        ) : (
          courses.map((course) => {
            const isExpanded = expandedCourses.has(course.id);
            const hasTopics = course.topics && course.topics.length > 0;
            const creatorName = course.createdByName || '';
            const creatorEmail = course.createdByEmail || '';
            const creatorDisplay = creatorName || creatorEmail || 'Unknown';
            const showCreatorIdNote = !creatorName && !creatorEmail && course.createdBy;
            
            return (
            <div key={course.id} className="course-row">
              {/* Course Row Header - Always Visible */}
              <div className="course-row-header" onClick={() => toggleCourseExpansion(course.id)}>
                <div className="course-row-info">
                  <div className="course-expand-icon">
                    {hasTopics ? (isExpanded ? '📂' : '📁') : '📄'}
                  </div>
                  <div className="course-basic-info">
                    <h3 className="course-title">
                      {course.courseType === 'programming' && '💻 '}
                      {course.courseType === 'mathematics' && '🔢 '}
                      {course.courseType === 'language' && '📖 '}
                      {course.courseType === 'robotics' && '🤖 '}
                      {course.courseType === 'science' && '🔬 '}
                      {course.courseType === 'arts' && '🎨 '}
                      {course.courseType === 'business' && '💼 '}
                      {!course.courseType && '📚 '}
                      {course.title}
                    </h3>
                    <div className="course-meta">
                      {course.courseType && (
                        <span className="meta-item course-type-badge">
                          {course.courseType.charAt(0).toUpperCase() + course.courseType.slice(1)}
                        </span>
                      )}
                      {course.category && (
                        <span className="meta-item category-badge">{course.category}</span>
                      )}
                      {course.level && (
                        <span className={`meta-item level-badge level-${course.level}`}>
                          {course.level === 'beginner' && '🌱'}
                          {course.level === 'intermediate' && '🌿'}
                          {course.level === 'advanced' && '🌳'}
                          {course.level === 'expert' && '🏆'}
                          {' '}{course.level.charAt(0).toUpperCase() + course.level.slice(1)}
                        </span>
                      )}
                      <span className="meta-item">{hasTopics ? `${course.topics.length} topics` : 'No topics'}</span>
                      <span className="meta-item status">🟢 Active</span>
                    </div>
                  </div>
                </div>
                <div className="course-row-actions" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => handleConfigureCourseComponents(course)} className="config-btn" title="Configure components">🎯</button>
                  <button onClick={() => handleEdit(course)} className="edit-btn" title="Edit course">✏️</button>
                  <button onClick={() => handleDelete(course.id)} className="delete-btn" title="Delete course">🗑️</button>
                </div>
              </div>

              {/* Expandable Content */}
              {isExpanded && (
                <div className="course-expanded-content">
                  {/* Course Details */}
                  <div className="course-details-expanded">
                    <div className="details-grid">
                      <div className="detail-card">
                        <h4>📋 Course Info</h4>
                        <p><strong>Type:</strong> {course.courseType ? course.courseType.charAt(0).toUpperCase() + course.courseType.slice(1) : 'Not specified'}</p>
                        {course.category && <p><strong>Category:</strong> {course.category}</p>}
                        {course.level && <p><strong>Level:</strong> {course.level.charAt(0).toUpperCase() + course.level.slice(1)}</p>}
                        <p><strong>Description:</strong> {course.description || 'No description'}</p>
                        <p>
                          <strong>Created by:</strong> {creatorDisplay}
                          {creatorName && creatorEmail && ` (${creatorEmail})`}
                        </p>
                        {showCreatorIdNote && (
                          <p className="creator-id-note">
                            <small>Creator ID: {course.createdBy}</small>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Topics List */}
                  <div className="topics-expanded">
                    <h4>📚 Course Topics</h4>
                    {course.topics.length === 0 ? (
                      <p className="no-topics-message">
                        No topics added yet. Add course topics to enable interactive content.
                      </p>
                    ) : (
                      <div className="topics-row-list">
                        {course.topics.map((topic, idx) => {
                          const topicHoursValue = parseFloat(topic.hours) || 0;
                          const topicHasMinHours = topicHoursValue >= 2;

                          return (
                          <div key={idx} className="topic-row">
                            {/* Topic Header */}
                            <div className="topic-row-header">
                              <div className="topic-row-info">
                                <span className="topic-expand-icon">📄</span>
                                <div className="topic-basic-info">
                                  <strong>{topic.name}</strong>
                                  {topic.hours && (
                                    <span className={`topic-hours ${topicHasMinHours ? '' : 'warning'}`}>
                                      {topicHoursValue}h
                                    </span>
                                  )}
                                  {topic.date && <span className="topic-date-badge">📅 {new Date(topic.date).toLocaleDateString()}</span>}
                                </div>
                              </div>
                              <div className="topic-row-actions">
                                <button 
                                  onClick={() => handleConfigureTopicComponents(course, idx)}
                                  className="topic-config-btn"
                                  title="Configure components for this topic"
                                >
                                  🎯
                                </button>
                              </div>
                            </div>

                            {/* Topic Details */}
                            <div className="topic-expanded-content">
                              {topic.description && (
                                <p className="topic-description">{topic.description}</p>
                              )}
                              {topic.sampleCode && (
                                <div className="topic-sample-code">
                                  <h5>💻 Sample Code:</h5>
                                  <pre><code>{topic.sampleCode}</code></pre>
                                </div>
                              )}

                              <div className="topic-content-section">
                                <div className="topic-content-header">
                                  <h5>➕ Add Interactive Learning Content</h5>
                                  <span className={`topic-hours-badge ${topicHasMinHours ? '' : 'warning'}`}>
                                    {topicHasMinHours ? `${topicHoursValue}h ready` : 'Need ≥ 2h'}
                                  </span>
                                </div>
                                {!topicHasMinHours && (
                                  <p className="topic-hours-warning">
                                    This topic needs at least 2 hours assigned before adding interactive content.
                                  </p>
                                )}
                                <div className="topic-content-grid">
                                  {courseContentButtons.map((btn) => (
                                    <button
                                      key={`${topic.name}-${btn.type}`}
                                      type="button"
                                      className="topic-content-btn"
                                      onClick={() => handleAddContentNavigation(course, topic.name, btn.type)}
                                      disabled={!topicHasMinHours}
                                    >
                                      {btn.label}
                                    </button>
                                  ))}
                                </div>
                                <small className="topic-content-hint">
                                  Opens the Interactive Learning workspace with this course and topic preselected.
                                </small>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            );
          })
        )}
      </div>

      {/* Component Configuration Modal */}
      {showComponentConfig && (
        <div className="modal-overlay" onClick={handleComponentConfigCancel}>
          <div className="modal-content component-config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {configuringTopic !== null 
                  ? `🎯 Configure Components for Topic: ${courses.find(c => c.id === configuringCourse)?.topics[configuringTopic]?.name}`
                  : `🎯 Configure Components for Course: ${courses.find(c => c.id === configuringCourse)?.title}`
                }
              </h2>
              <button className="close-btn" onClick={handleComponentConfigCancel}>❌</button>
            </div>
            <div className="modal-body">
              <CourseComponentsManager
                courseId={configuringTopic === null ? configuringCourse : null}
                topicId={configuringTopic !== null ? `${configuringCourse}-${configuringTopic}` : null}
                onSave={handleComponentConfigSave}
                onCancel={handleComponentConfigCancel}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CoursesManagement;
